import { useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchPreferences, updatePreferences, type UserPreferences } from './meApi';
import { useAuthStore } from '@/features/auth/store';

/**
 * The one place the application reads a person's display preferences.
 *
 * Before this, `UserPreference` was written by the Settings page and read by exactly one file —
 * the theme store. Density, sidebar, timezone, date format, time format, locale, landing page,
 * working days and diary view were saved successfully and consumed by nothing. Settings said one
 * thing and the product did another.
 *
 * Every consumer shares ONE React Query key, so a page rendering twenty dates makes one request
 * and every surface agrees about what the preferences are (CLAUDE.md rule 14).
 *
 * SCOPE, which is the part that matters in a chambers:
 *   - `timezone` and `locale` are the PERSON's. They may be null, meaning "inherit the practice".
 *   - `effective.*` is what actually applies once that inheritance is resolved — computed on the
 *     SERVER, never here, so the client cannot invent a different answer to the same question.
 *   - Nothing in this file writes an organisation setting. A personal preference changes how one
 *     person SEES the practice; it never changes what the practice means.
 */

export const PREFERENCES_QUERY_KEY = ['me-preferences'] as const;

/**
 * What the product falls back to before the account's preferences have loaded, and if they
 * cannot be loaded at all. These mirror the column defaults in `UserPreference` — they are not a
 * second opinion about what a default is.
 */
export const PREFERENCE_FALLBACK: UserPreferences = {
  theme: 'light',
  density: 'comfortable',
  sidebarCollapsed: false,
  timezone: null,
  locale: null,
  dateFormat: 'dd/MM/yyyy',
  timeFormat: '24h',
  landingPage: null,
  diaryDefaultView: null,
  diaryDefaultRange: null,
  workingDays: [1, 2, 3, 4, 5],
  workingHoursStart: null,
  workingHoursEnd: null,
  initialised: false,
  effective: {
    // Europe/London is the practice default the server also falls back to; it is NOT a guess
    // made here. It only ever applies for the moments before the account answers.
    timezone: 'Europe/London',
    locale: 'en-GB',
    timezoneInherited: true,
    localeInherited: true,
  },
};

/**
 * The signed-in person's preferences.
 *
 * Returns the fallback rather than undefined while loading, so no surface has to decide what to
 * render "before preferences arrive" — a date is formatted with the practice default for a
 * moment and then settles. Never blocks a page.
 */
export function usePreferences(): UserPreferences {
  const authenticated = useAuthStore((s) => !!s.user);
  const { data } = useQuery({
    queryKey: PREFERENCES_QUERY_KEY,
    queryFn: fetchPreferences,
    enabled: authenticated,
    staleTime: 5 * 60_000,
    // A preference is a convenience. If it cannot be read, the product must still work.
    retry: 1,
  });
  return data ?? PREFERENCE_FALLBACK;
}

/**
 * Save a change and update every consumer at once.
 *
 * Only ever the caller's OWN preferences: the endpoint is `/me/preferences` and takes no user
 * id, so there is no shape of this call that could write somebody else's.
 */
export function useUpdatePreferences() {
  const qc = useQueryClient();
  return async (patch: Partial<Omit<UserPreferences, 'effective'>>): Promise<UserPreferences> => {
    const updated = await updatePreferences(patch);
    qc.setQueryData(PREFERENCES_QUERY_KEY, updated);
    return updated;
  };
}

/** Discard the cached preferences — used at sign-out so the next person starts clean. */
export function forgetPreferences(qc: ReturnType<typeof useQueryClient>): void {
  qc.removeQueries({ queryKey: PREFERENCES_QUERY_KEY });
}
